// src/pages/BlogPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiTag } from 'react-icons/fi';
import { BlogService } from '../services/blog.service';
import type { BlogCategory, BlogPost } from '../types/Blog';

export default function BlogPage() {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const fetchPosts = (next?: { q?: string; categoryId?: string }) => {
    setLoading(true);
    BlogService.getPublicPosts({
      q: next?.q ?? q,
      categoryId: next?.categoryId ?? categoryId,
      limit: 20
    })
      .then(res => setPosts(Array.isArray(res) ? res : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    BlogService.getPublicCategories()
      .then(res => setCategories(Array.isArray(res) ? res : []))
      .catch(() => setCategories([]));
    fetchPosts({ q: '', categoryId: '' });
  }, []);

  const filteredCategory = useMemo(
    () => categories.find(c => String(c.id) === String(categoryId)) || null,
    [categories, categoryId]
  );

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 16px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0 }}>Blog</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--color-texto-secundario)' }}>
            {filteredCategory ? `Categoría: ${filteredCategory.name}` : 'Artículos y novedades'}
          </p>
        </div>

        <Link to="/" style={{ textDecoration: 'none', color: 'var(--color-primario)', fontWeight: 800 }}>
          Volver
        </Link>
      </header>

      <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 16,
            border: '1px solid var(--color-gris-claro)',
            background: 'var(--color-blanco)',
            flex: '1 1 320px',
            boxShadow: 'var(--sombra-elevacion-1)'
          }}
        >
          <FiSearch />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar..."
            style={{ border: 'none', outline: 'none', width: '100%', background: 'transparent' }}
            onKeyDown={e => e.key === 'Enter' && fetchPosts({ q: q.trim(), categoryId })}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 16,
            border: '1px solid var(--color-gris-claro)',
            background: 'var(--color-blanco)',
            boxShadow: 'var(--sombra-elevacion-1)'
          }}
        >
          <FiTag />
          <select
            value={categoryId}
            onChange={e => {
              setCategoryId(e.target.value);
              fetchPosts({ q: q.trim(), categoryId: e.target.value });
            }}
            style={{ border: 'none', outline: 'none', background: 'transparent' }}
          >
            <option value="">Todas</option>
            {categories.map(c => (
              <option key={String(c.id)} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => fetchPosts({ q: q.trim(), categoryId })}
          style={{
            border: 'none',
            borderRadius: 16,
            padding: '12px 16px',
            background: 'var(--color-primario)',
            color: '#fff',
            fontWeight: 800,
            cursor: 'pointer',
            boxShadow: 'var(--sombra-elevacion-1)'
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <div style={{ marginTop: 22 }}>Cargando...</div>
      ) : posts.length === 0 ? (
        <div style={{ marginTop: 22, color: 'var(--color-texto-secundario)' }}>No hay publicaciones.</div>
      ) : (
        <div style={{ marginTop: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {posts.map(p => (
            <Link
              key={String(p.id)}
              to={`/blog/${p.slug ?? p.id}`}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                background: 'var(--color-blanco)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: 24,
                overflow: 'hidden',
                boxShadow: 'var(--sombra-elevacion-2)'
              }}
            >
              {p.cover_image_url && (
                <img
                  src={p.cover_image_url}
                  alt={p.title}
                  style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                  onError={e => (e.currentTarget.style.display = 'none')}
                />
              )}
              <div style={{ padding: 16 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, lineHeight: 1.2 }}>{p.title}</h3>
                <p style={{ margin: '10px 0 0', color: 'var(--color-texto-secundario)', lineHeight: 1.45 }}>
                  {p.excerpt || ''}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
